import type { CreateFolderInput, FolderDto, FolderKind, UpdateFolderInput } from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

/**
 * Folders come in two independent trees per project: one for test cases, one
 * for checklists. Every call below works inside a single tree — a screen only
 * ever sees the folders of the content it lists.
 */
export const folderTreeQueryOptions = (projectCode: string, kind: FolderKind) =>
  queryOptions({
    queryKey: queryKeys.folders.tree(projectCode, kind),
    queryFn: ({ signal }) =>
      api.get<FolderDto[]>(`/projects/${projectCode}/folders`, { kind }, signal),
  });

export const useFolderTree = (projectCode: string, kind: FolderKind) =>
  useQuery(folderTreeQueryOptions(projectCode, kind));

const useFolderInvalidation = (projectCode: string, kind: FolderKind) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.folders.tree(projectCode, kind) });
    // The list beside the tree shows folder names, and deleting a folder can
    // move or remove its contents.
    void queryClient.invalidateQueries({
      queryKey:
        kind === 'case'
          ? queryKeys.testCases.all(projectCode)
          : queryKeys.checklists.all(projectCode),
    });
  };
};

export const useCreateFolder = (projectCode: string, kind: FolderKind) => {
  const invalidate = useFolderInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: (input: Omit<CreateFolderInput, 'kind'>) =>
      api.post<FolderDto>(`/projects/${projectCode}/folders`, { ...input, kind }),
    onSuccess: invalidate,
  });
};

export const useUpdateFolder = (projectCode: string, kind: FolderKind) => {
  const invalidate = useFolderInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: ({ folderId, input }: { folderId: string; input: UpdateFolderInput }) =>
      api.patch<FolderDto[]>(`/projects/${projectCode}/folders/${folderId}`, input),
    onSuccess: invalidate,
  });
};

export const useDeleteFolder = (projectCode: string, kind: FolderKind) => {
  const invalidate = useFolderInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: ({
      folderId,
      strategy,
      targetFolderId,
    }: {
      folderId: string;
      strategy: 'move' | 'cascade';
      /** Where the contents go when they are kept. */
      targetFolderId?: string;
    }) =>
      api.delete(`/projects/${projectCode}/folders/${folderId}`, {
        strategy,
        ...(targetFolderId ? { targetFolderId } : {}),
      }),
    onSuccess: invalidate,
  });
};

/** Flattens the tree into indented options for folder pickers. */
export const flattenFolders = (
  folders: FolderDto[],
  depth = 0,
): Array<{ id: string; label: string }> =>
  folders.flatMap((folder) => [
    { id: folder.id, label: `${'— '.repeat(depth)}${folder.name}` },
    ...flattenFolders(folder.children, depth + 1),
  ]);
